'use client';
import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Vehicle {
  id: string;
  name: string;
  category: string;
  price: number;
  eta: string;
  capacity: string;
  image: string;
  imageAlt: string;
  features: string[];
}

const vehicles: Vehicle[] = [
{
  id: 'select_basic',
  name: 'Basic Scooter',
  category: 'Standard',
  price: 3,
  eta: '2 min',
  capacity: '1 rider',
  image: "https://images.unsplash.com/photo-1631007230024-b2076e87f951",
  imageAlt: 'Black electric scooter with LED display',
  features: ['15 mph max', 'LED lights']
},
{
  id: 'select_premium',
  name: 'Premium Scooter',
  category: 'Enhanced',
  price: 4,
  eta: '3 min',
  capacity: '1 rider',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_165fe94d1-1767510456105.png",
  imageAlt: 'Red premium electric scooter with suspension',
  features: ['20 mph max', 'Suspension', 'Premium comfort']
},
{
  id: 'select_cargo',
  name: 'Cargo Scooter',
  category: 'Heavy Duty',
  price: 5,
  eta: '5 min',
  capacity: '1 rider + cargo',
  image: "https://images.unsplash.com/photo-1714550454721-a7f6624c09d2",
  imageAlt: 'White cargo electric scooter with large basket',
  features: ['18 mph max', 'Large basket', 'Cargo space']
}];


export default function VehicleSelector() {
  const [mounted, setMounted] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) =>
        <div key={`skeleton_${i}`} className="card p-6 animate-pulse">
            <div className="flex gap-4">
              <div className="w-32 h-24 bg-muted rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="h-6 bg-muted rounded w-1/3" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </div>
            </div>
          </div>
        )}
      </div>);

  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-display font-bold text-primary mb-2">
          Select Your Scooter
        </h2>
        <p className="text-muted-foreground font-body">
          Choose the electric scooter that best fits your needs
        </p>
      </div>

      {/* Vehicle Cards */}
      <div className="space-y-4">
        {vehicles.map((vehicle) =>
        <div
          key={vehicle.id}
          onClick={() => setSelectedVehicle(vehicle.id)}
          className={`card p-6 cursor-pointer transition-all ${
          selectedVehicle === vehicle.id ?
          'ring-2 ring-accent shadow-glow-accent' :
          'hover:shadow-lg'}`
          }>
          
            <div className="flex flex-col md:flex-row gap-6">
              {/* Vehicle Image */}
              <div className="relative w-full md:w-48 h-36 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                <AppImage
                src={vehicle.image}
                alt={vehicle.imageAlt}
                className="w-full h-full object-cover" />
              
                {selectedVehicle === vehicle.id &&
              <div className="absolute inset-0 bg-accent/20 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center">
                      <Icon name="CheckIcon" size={24} className="text-accent-foreground" />
                    </div>
                  </div>
              }
              </div>

              {/* Vehicle Info */}
              <div className="flex-1 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-display font-bold text-primary">
                      {vehicle.name}
                    </h3>
                    <p className="text-sm text-accent font-body font-semibold">
                      {vehicle.category}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-display font-bold text-primary">
                      ${vehicle.price}
                    </p>
                    <p className="text-xs text-muted-foreground font-body">
                      unlock fee
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Icon name="ClockIcon" size={16} className="text-secondary" />
                    <span className="font-body text-foreground/70">{vehicle.eta} away</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="UserIcon" size={16} className="text-secondary" />
                    <span className="font-body text-foreground/70">{vehicle.capacity}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2">
                  {vehicle.features.map((feature, idx) =>
                <span
                  key={`${vehicle.id}_feat_${idx}`}
                  className="px-3 py-1 rounded-full bg-muted text-xs font-body text-foreground/70">
                  
                      {feature}
                    </span>
                )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Continue Button */}
      <button
        disabled={!selectedVehicle}
        className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
        
        Continue to Payment
      </button>
    </div>);

}